import {AgentType, LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatFileUpload} from 'llm/chat-library/ChatLibraryTypes';
import type {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import {assertNever} from 'util/Types';

import {
  ClientChatEvent,
  ClientDisplayChatEvent,
  ClientOptimisticUserMessage,
  ClientProgressMessage,
  ClientStreamingAssistantMessage,
  DisplayMessageAssistantResponseType,
  DisplayMessageChatEventNode,
  ProgressStatus,
} from './MessageTypes';

export function filterDisplayEvents(events: Array<ClientChatEvent>): Array<ClientDisplayChatEvent> {
  return events.filter(
    (event): event is ClientDisplayChatEvent =>
      event.__typename === 'DisplayMessageChatEventNode' ||
      event.__typename === 'ClientOptimisticUserMessage' ||
      event.__typename === 'ClientProgressMessage' ||
      event.__typename === 'ClientStreamingAssistantMessage'
  );
}

/** Short user-facing label for save flows and filenames ("Message" vs "Report"). */
export function getMessageResponseTypeAsText(
  responseType: DisplayMessageAssistantResponseType
): 'Message' | 'Report' {
  switch (responseType) {
    case DisplayMessageAssistantResponseType.REPLY:
      return 'Message';
    case DisplayMessageAssistantResponseType.REPORT:
      return 'Report';
    default:
      assertNever(responseType);
      return 'Message';
  }
}

export function getDisplayMessageAssistantResponseType(
  event: DisplayMessageChatEventNode | ClientStreamingAssistantMessage
): DisplayMessageAssistantResponseType {
  // Prefer the persisted displayType from message blocks when available.
  const hasReportBlock = event.messageBlocks.some(
    (block) => block.__typename === 'DisplayTextBlockNode' && block.displayType === 'report'
  );
  if (hasReportBlock) {
    return DisplayMessageAssistantResponseType.REPORT;
  }

  // Fall back to agent type for backward compatibility (existing messages without displayType).
  switch (event.agentType) {
    case AgentType.DEEP_RESEARCH:
      return DisplayMessageAssistantResponseType.REPORT;
    case AgentType.ASK:
    case AgentType.DATA_CAPTURE:
    case AgentType.LEGACY_DATA_IMPORT:
    case AgentType.ANALYSIS:
    case AgentType.CO_SCIENTIST:
    case AgentType.ASSISTANT:
    case AgentType.ONBOARDING:
      return DisplayMessageAssistantResponseType.REPLY;
    default:
      assertNever(event.agentType);
      return DisplayMessageAssistantResponseType.REPLY;
  }
}

export function createProgressMessage({
  status,
  errorMessage,
  agentType,
  progressData,
  associatedMessageId,
}: {
  status: ProgressStatus;
  errorMessage: string | null;
  agentType: AgentType | null;
  progressData: ProgressData | null;
  associatedMessageId?: string;
}): ClientProgressMessage {
  const message: ClientProgressMessage = {
    __typename: 'ClientProgressMessage',
    status,
    errorMessage,
    agentType,
    role: null,
    progressData,
  };
  // Only include associatedMessageId if provided (for completed thinking sections)
  if (associatedMessageId !== undefined) {
    message.associatedMessageId = associatedMessageId;
  }
  return message;
}

export function createStreamingMessage({
  createdAt,
  agentType,
  responseToEventId,
  streamingContent,
  displayType = null,
  progressMessage = null,
}: {
  createdAt: string;
  agentType: AgentType;
  responseToEventId: string;
  streamingContent: string;
  displayType?: string | null;
  progressMessage?: string | null;
}): ClientStreamingAssistantMessage {
  return {
    __typename: 'ClientStreamingAssistantMessage',
    responseToEventId,
    messageBlocks: [
      {
        __typename: 'DisplayTextBlockNode',
        text: streamingContent,
        displayType,
      },
    ],
    role: LLMRole.ASSISTANT,
    agentType,
    createdAt,
    progressMessage,
  };
}

export function createPendingUserMessage({
  text,
  fileUploads,
  agentType,
  replyToId,
  retryOfId,
}: {
  text: string;
  fileUploads: Array<ChatFileUpload>;
  agentType: AgentType;
  /** The ID of the last display message (user or assistant) at the time this optimistic message was sent. */
  replyToId: string | null;
  /** If this is a retry, the ID of the original user message being retried. */
  retryOfId: string | null;
}): ClientOptimisticUserMessage {
  return {
    __typename: 'ClientOptimisticUserMessage',
    role: LLMRole.USER,
    agentType,
    messageBlocks: [
      {
        __typename: 'DisplayTextBlockNode',
        text,
        displayType: null,
      },
      ...fileUploads.map((fileUpload) => ({
        __typename: 'DisplayFileBlockNode' as const,
        filename: fileUpload.filename,
        mediaType: fileUpload.mediaType,
      })),
    ],
    replyToId,
    retryOfId,
  };
}
